import { SwitchType, TrackedSwitch } from '@domain'
import {
	SwitchTrackingChartRes,
	SwitchTrackingListRes,
	TrackedSwitchCustomer,
	TrackedSwitchDetail,
	TrackedSwitchDetailFrom,
	TrackedSwitchDetailTo,
	TrackedSwitchMarketBasket,
} from '@services/switchTracking.types'
import dayjs from 'dayjs'

const currentYear = dayjs().year()

const monthlyRebates = [
	4120.55, 5210.19, 3890.42, 7455.01, 6120.73, 9210.88, 8455.12, 7120.6, 10233.47, 9877.31, 6540.09,
	5122.84,
]

export const switchTrackingChart: SwitchTrackingChartRes = {
	year: currentYear,
	currency: 'USD',
	totalRebate: monthlyRebates.reduce((sum, rebate) => sum + rebate, 0),
	months: monthlyRebates.map((rebate, index) => ({ month: index + 1, rebate })),
}

export const switchTrackingRows: TrackedSwitch[] = [
	{
		id: 'a1f0c3d2-0001-4c1a-9f10-1c2b3d4e5f60',
		switchDate: `${currentYear}-01-14`,
		switchType: SwitchType.Verified,
		currency: 'USD',
		monthlyRebate: 1284.5678,
		monthlyRebateActual: 902.1133,
		annualPeriodMonths: 12,
		annualRebate: 15414.8136,
		customerLocation: 'Picard Bistro - Labarre',
		customerParent: 'La Barre Hospitality Group',
		from: {
			mpc: 'TAT-88421',
			manufacturer: 'Tatooine Provisions',
			description: 'Bantha Ribeye, Center Cut, Choice Grade, Vacuum Sealed, Frozen',
		},
		to: {
			mpc: 'ROX-10293',
			manufacturer: 'Roxy Music Foods',
			description: 'Avalon Ribeye, Center Cut, Upper Choice, Wet Aged 21 Days',
		},
	},
	{
		id: 'a1f0c3d2-0003-4c1a-9f10-1c2b3d4e5f60',
		switchDate: `${currentYear}-02-21`,
		switchType: SwitchType.Stale,
		currency: 'USD',
		monthlyRebate: 318.0025,
		monthlyRebateActual: 318.0025,
		annualPeriodMonths: 12,
		annualRebate: 3816.03,
		customerLocation: 'Ten Forward - Deck 10',
		customerParent: 'Enterprise Dining Services',
		from: {
			mpc: 'NAB-31200',
			manufacturer: 'Naboo Fresh Farms',
			description: 'Shaak Roast, Boneless, Chilled',
		},
		to: {
			mpc: 'SPK-49001',
			manufacturer: 'Sparks Seafood',
			description: 'Kimono My House Cod Loin, Skinless, IQF',
		},
	},
	{
		id: 'a1f0c3d2-0009-4c1a-9f10-1c2b3d4e5f60',
		switchDate: `${currentYear}-08-19`,
		switchType: SwitchType.Expired,
		currency: 'USD',
		monthlyRebate: null,
		monthlyRebateActual: null,
		annualPeriodMonths: null,
		annualRebate: null,
		customerLocation: 'Quark’s Bar - Promenade',
		customerParent: 'Deep Space Hospitality',
		from: {
			mpc: 'TAT-70019',
			manufacturer: 'Tatooine Provisions',
			description: 'Blue Milk, Ultra Pasteurized, 6/1 GAL',
		},
		to: {
			mpc: 'ENO-33150',
			manufacturer: 'Eno Dairy Collective',
			description: 'Another Green World Oat Beverage, Barista Blend, 12/32 OZ',
		},
	},
]

/**
 * `kpi` and `counts` cover every switch type whatever was filtered, so they are
 * deliberately larger than the rows here rather than derived from them.
 */
export const switchTrackingList: SwitchTrackingListRes = {
	data: switchTrackingRows,
	currency: 'USD',
	kpi: {
		verified: 2500000.22,
		unverified: 578000.01,
		stale: 94231.32,
		expired: 12045.1,
	},
	counts: {
		verified: 22,
		unverified: 37,
		stale: 6,
		expired: 3,
	},
	pagination: {
		page: 1,
		pageSize: 25,
		total: switchTrackingRows.length,
		totalPages: 1,
	},
}

export const switchTrackingDetail: TrackedSwitchDetail = {
	id: switchTrackingRows[0].id,
	switchType: SwitchType.Verified,
	currency: 'USD',
	switchDate: `${currentYear}-01-14`,
	detectedAt: `${currentYear}-01-20T10:00:00Z`,
	marketBasket: {
		id: 48213,
		name: 'La Barre Hospitality Group - Center of the Plate',
		annualPeriodMonths: 12,
		completedDate: `${currentYear}-01-10`,
		salesforceCaseNumber: 'CASE-00471138',
		assignee: 'Jean-Luc Picard',
		brand: 'Foodbuy',
		tier: 'Tier 2',
	},
	customer: {
		name: 'Picard Bistro',
		plid: 'PLID-0099421',
		dmid: 5541200,
		parent: 'La Barre Hospitality Group',
		location: 'Picard Bistro - Labarre',
	},
	monthlyRebate: 1284.5678,
	annualRebate: 15414.8136,
	originalDeviationPercent: 0.75,
	switchDeviationPercent: 0.2,
	from: {
		manufacturerProductDmid: 8811223,
		mpc: 'TAT-88421',
		description:
			'Bantha Ribeye, Center Cut, Choice Grade, Vacuum Sealed, Frozen, Individually Wrapped for Extended Hold',
		manufacturer: 'Tatooine Provisions',
		manufacturerPlid: 'MA-8811223',
		brandName: 'Twin Suns Reserve',
		brandType: 'National',
		// market_basket_purchases has no brand owner column
		brandOwner: null,
		distributorName: 'Jawa Distribution - Anchorhead',
		distributorParentName: 'Jawa Distribution (AZ)',
		distributorLocationDmid: 3320011,
		distributorLocationPlid: 'PLID-3320011',
		dpc: 'DPC-441029',
		packSize: '4/5 LB',
		unit: 'CS',
		category: 'Beef Whole Muscle',
		quantity: 24,
		unitPrice: 8.7452,
		packConvert: 1,
		totalPrice: 209.8848,
	},
	to: {
		manufacturerProductDmid: 9922114,
		mpc: 'ROX-10293',
		description:
			'Avalon Ribeye, Center Cut, Upper Choice, Wet Aged 21 Days, Vacuum Sealed, Case Ready for Banquet Service',
		manufacturer: 'Roxy Music Foods',
		manufacturerPlid: 'MA-9922114',
		brandName: 'Avalon Select',
		brandType: 'Manufacturer',
		brandOwner: 'La Barre Hospitality Group',
		distributorName: 'Eno Foodservice - Portsmouth',
		distributorParentName: 'Eno Foodservice (NH)',
		// switch_candidates only carries distributor_plid
		distributorLocationDmid: null,
		distributorLocationPlid: 'PLID-3320055',
		dpc: 'DPC-778120',
		packSize: '4/4 LB',
		unit: 'CS',
		category: 'Beef Whole Muscle',
		packConversion: 1.25,
		caseQty: 19,
		annualCaseQty: 228,
		unitDeviatedPrice: 7.9911,
		totalDeviatedPrice: 151.8309,
		rebatePerCase: 67.6088,
		annualRebate: 15414.8136,
	},
}

export function makeTrackedSwitch(overrides: Partial<TrackedSwitch> = {}): TrackedSwitch {
	return { ...switchTrackingRows[0], ...overrides }
}

/** The nested objects merge field by field, so a variant only states what it changes. */
type TrackedSwitchDetailOverrides = Partial<
	Omit<TrackedSwitchDetail, 'marketBasket' | 'customer' | 'from' | 'to'>
> & {
	marketBasket?: Partial<TrackedSwitchMarketBasket>
	customer?: Partial<TrackedSwitchCustomer>
	from?: Partial<TrackedSwitchDetailFrom>
	to?: Partial<TrackedSwitchDetailTo>
}

export function makeTrackedSwitchDetail(
	overrides: TrackedSwitchDetailOverrides = {}
): TrackedSwitchDetail {
	const { marketBasket, customer, from, to, ...rest } = overrides

	return {
		...switchTrackingDetail,
		...rest,
		marketBasket: { ...switchTrackingDetail.marketBasket, ...marketBasket },
		customer: { ...switchTrackingDetail.customer, ...customer },
		from: { ...switchTrackingDetail.from, ...from },
		to: { ...switchTrackingDetail.to, ...to },
	}
}
